import React from 'react';

/**
 * One professional line-icon set for the whole app.
 *
 * These replace the cartoonish colour emoji (📋 🐛 🎉 🙈 …) that were scattered through the
 * product surface — auth pages, the command palette, notifications, the activity feed, empty
 * states. Emoji render differently on every platform, carry their own colour that fights the
 * theme, and read as unfinished; a single stroke-based set inherits `currentColor`, scales
 * cleanly, and looks the same everywhere.
 *
 * Every glyph is drawn on a 24×24 viewbox with `stroke="currentColor"`, so colour comes from
 * the surrounding text and both light and dark themes are handled for free. Pass `size` to
 * scale. Decorative by default (`aria-hidden`), since these sit beside real text labels; pass
 * a `title` only when the icon is the sole meaning.
 */
export type IconName =
  | 'tasks' | 'clock' | 'chart' | 'users' | 'user' | 'bug' | 'folder' | 'home'
  | 'checkCircle' | 'check' | 'sparkle' | 'fileText' | 'palette' | 'fileCode'
  | 'calendar' | 'gear' | 'bell' | 'plus' | 'pencil' | 'trash' | 'refresh'
  | 'arrowRight' | 'message' | 'undo' | 'trendingDown' | 'mail' | 'userPlus'
  | 'userMinus' | 'key' | 'eye' | 'eyeOff' | 'list' | 'grid' | 'warning' | 'x'
  | 'atSign' | 'search' | 'globe' | 'gantt' | 'trendingUp' | 'paperclip';

const PATHS: Record<IconName, React.ReactNode> = {
  // A single stroked path: these are drawn with `fill="none"` and a shared stroke, so a
  // filled shape would render as a solid blob beside the rest.
  paperclip: <path d="M21 11.5l-8.8 8.8a5 5 0 01-7.1-7.1l8.8-8.8a3.3 3.3 0 014.7 4.7l-8.8 8.8a1.7 1.7 0 01-2.4-2.4l8.1-8.1" />,
  tasks: <><rect x="4" y="3" width="16" height="18" rx="2" /><path d="M8 3.5h8" /><path d="M8.5 11l2.2 2.2L15 9" /></>,
  clock: <><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3.5 2" /></>,
  chart: <><path d="M3 3v18h18" /><rect x="7" y="12" width="3" height="6" /><rect x="12.5" y="8" width="3" height="10" /><rect x="18" y="5" width="3" height="13" /></>,
  users: <><path d="M17 21v-2a4 4 0 00-4-4H6a4 4 0 00-4 4v2" /><circle cx="9.5" cy="7" r="4" /><path d="M22 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75" /></>,
  user: <><path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2" /><circle cx="12" cy="7" r="4" /></>,
  bug: <><rect x="8" y="6" width="8" height="12" rx="4" /><path d="M12 6V4M9 8L6.5 5.5M15 8l2.5-2.5M8 11H4M20 11h-4M8.5 15L5 17.5M15.5 15l3.5 2.5" /></>,
  folder: <><path d="M3 7a2 2 0 012-2h4l2 2h8a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2z" /></>,
  home: <><path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z" /><polyline points="9 22 9 12 15 12 15 22" /></>,
  checkCircle: <><circle cx="12" cy="12" r="9" /><path d="M8.5 12l2.4 2.4L16 9.5" /></>,
  check: <><path d="M4 12.5l5 5L20 6.5" /></>,
  sparkle: <><path d="M12 3l1.9 5.1L19 10l-5.1 1.9L12 17l-1.9-5.1L5 10l5.1-1.9z" /></>,
  fileText: <><path d="M14 3H7a2 2 0 00-2 2v14a2 2 0 002 2h10a2 2 0 002-2V8z" /><path d="M14 3v5h5M9 13h6M9 17h6" /></>,
  palette: <><path d="M12 3a9 9 0 000 18c1.1 0 1.5-.9 1.5-1.6 0-.9-.7-1.4-.7-2.2 0-.7.6-1.2 1.4-1.2H16a5 5 0 005-5c0-4.4-4-8-9-8z" /><circle cx="7.5" cy="10.5" r="1" /><circle cx="12" cy="7.5" r="1" /><circle cx="16.5" cy="10.5" r="1" /></>,
  fileCode: <><path d="M14 3H7a2 2 0 00-2 2v14a2 2 0 002 2h10a2 2 0 002-2V8z" /><path d="M14 3v5h5M10 12l-2 2 2 2M14 12l2 2-2 2" /></>,
  calendar: <><rect x="3" y="4" width="18" height="17" rx="2" /><path d="M16 2v4M8 2v4M3 10h18" /></>,
  gear: <><circle cx="12" cy="12" r="3" /><path d="M19.4 13a1.6 1.6 0 00.3 1.8l.1.1a2 2 0 11-2.8 2.8l-.1-.1a1.6 1.6 0 00-2.7 1.1V21a2 2 0 11-4 0v-.1A1.6 1.6 0 007.3 19a1.6 1.6 0 00-1.8.3l-.1.1a2 2 0 11-2.8-2.8l.1-.1a1.6 1.6 0 00-1.1-2.7H1a2 2 0 110-4h.1A1.6 1.6 0 003 7.3a1.6 1.6 0 00-.3-1.8l-.1-.1a2 2 0 112.8-2.8l.1.1a1.6 1.6 0 001.8.3H9a1.6 1.6 0 001-1.5V1a2 2 0 114 0v.1a1.6 1.6 0 001 1.5 1.6 1.6 0 001.8-.3l.1-.1a2 2 0 112.8 2.8l-.1.1a1.6 1.6 0 00-.3 1.8V9a1.6 1.6 0 001.5 1H23a2 2 0 110 4h-.1a1.6 1.6 0 00-1.5 1z" /></>,
  bell: <><path d="M18 8A6 6 0 006 8c0 7-3 9-3 9h18s-3-2-3-9M13.7 21a2 2 0 01-3.4 0" /></>,
  plus: <><path d="M12 5v14M5 12h14" /></>,
  pencil: <><path d="M12 20h9" /><path d="M16.5 3.5a2.1 2.1 0 013 3L7 19l-4 1 1-4z" /></>,
  trash: <><path d="M3 6h18M8 6V4a2 2 0 012-2h4a2 2 0 012 2v2M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6" /></>,
  refresh: <><path d="M21 12a9 9 0 11-2.6-6.4L21 8" /><path d="M21 3v5h-5" /></>,
  arrowRight: <><path d="M5 12h14M13 6l6 6-6 6" /></>,
  message: <><path d="M21 11.5a8.4 8.4 0 01-9 8 9.4 9.4 0 01-4-.9L3 20l1.4-4.2A8 8 0 013 11.5a8.5 8.5 0 019-8 8.4 8.4 0 019 8z" /></>,
  undo: <><path d="M9 14L4 9l5-5" /><path d="M4 9h11a5 5 0 015 5v0a5 5 0 01-5 5H9" /></>,
  trendingDown: <><path d="M23 18l-9.5-9.5-5 5L1 6" /><path d="M17 18h6v-6" /></>,
  mail: <><rect x="3" y="5" width="18" height="14" rx="2" /><path d="M4 7l8 6 8-6" /></>,
  userPlus: <><path d="M15 21v-2a4 4 0 00-4-4H6a4 4 0 00-4 4v2" /><circle cx="8.5" cy="7" r="4" /><path d="M19 8v6M22 11h-6" /></>,
  userMinus: <><path d="M15 21v-2a4 4 0 00-4-4H6a4 4 0 00-4 4v2" /><circle cx="8.5" cy="7" r="4" /><path d="M22 11h-6" /></>,
  key: <><circle cx="7.5" cy="15.5" r="3.5" /><path d="M10 13l9-9M17 6l2 2M14 9l2 2" /></>,
  eye: <><path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7z" /><circle cx="12" cy="12" r="3" /></>,
  eyeOff: <><path d="M9.9 5.2A9.6 9.6 0 0112 5c6.5 0 10 7 10 7a13.2 13.2 0 01-2.4 3.1M6.2 6.2A13 13 0 002 12s3.5 7 10 7a9.5 9.5 0 004-.8" /><path d="M9.9 9.9a3 3 0 004.2 4.2M3 3l18 18" /></>,
  list: <><path d="M8 6h13M8 12h13M8 18h13M3 6h.01M3 12h.01M3 18h.01" /></>,
  grid: <><rect x="3" y="3" width="8" height="8" rx="1" /><rect x="13" y="3" width="8" height="8" rx="1" /><rect x="3" y="13" width="8" height="8" rx="1" /><rect x="13" y="13" width="8" height="8" rx="1" /></>,
  warning: <><path d="M10.3 3.9L1.8 18a2 2 0 001.7 3h17a2 2 0 001.7-3L14.7 3.9a2 2 0 00-3.4 0z" /><path d="M12 9v4M12 17h.01" /></>,
  x: <><path d="M18 6L6 18M6 6l12 12" /></>,
  atSign: <><circle cx="12" cy="12" r="4" /><path d="M16 8v5a3 3 0 006 0v-1a10 10 0 10-3.9 7.9" /></>,
  search: <><circle cx="11" cy="11" r="7" /><path d="M21 21l-4.35-4.35" /></>,
  globe: <><circle cx="12" cy="12" r="9" /><path d="M3 12h18M12 3a15 15 0 010 18M12 3a15 15 0 000 18" /></>,
  gantt: <><rect x="3" y="5" width="10" height="3" rx="1.5" /><rect x="8" y="10.5" width="11" height="3" rx="1.5" /><rect x="5" y="16" width="8" height="3" rx="1.5" /></>,
  trendingUp: <><path d="M23 6l-9.5 9.5-5-5L1 18" /><path d="M17 6h6v6" /></>,
};

export interface IconProps {
  name: IconName;
  size?: number;
  className?: string;
  strokeWidth?: number;
  title?: string;
  style?: React.CSSProperties;
}

export const Icon: React.FC<IconProps> = ({
  name,
  size = 18,
  className,
  strokeWidth = 1.8,
  title,
  style,
}) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={strokeWidth}
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
    style={style}
    role={title ? 'img' : undefined}
    aria-hidden={title ? undefined : true}
    aria-label={title}
    focusable="false"
  >
    {title && <title>{title}</title>}
    {PATHS[name]}
  </svg>
);

export default Icon;
